import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import Whiteboard from '../components/Whiteboard';
import SpellingTest from '../components/SpellingTest';

const colours = {
  pink: '#FBDDE6', blue: '#d0f0ec', purple: '#FBDDE6', green: '#d0f0ec', yellow: '#FFC84B33',
  purpleMid: '#20396B', blueMid: '#36B7A6', greenMid: '#36B7A6', pinkMid: '#F2A7C0',
};

export default function LiveSession({ sessionData, role, studentName, studentId, onEnd }) {
  const [tab, setTab] = useState('materials');
  const [isLocked, setIsLocked] = useState(false);
  const [postits, setPostits] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [materials, setMaterials] = useState([]);
  const [activePdf, setActivePdf] = useState(null);
  const [mistakes, setMistakes] = useState([]);
  const [newMistake, setNewMistake] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');

  const sessionId = sessionData?.id;
  const student = sessionData?.student_name || studentName;

  useEffect(() => {
    fetchPostits();
    fetchMaterials();
    fetchMistakes();

    const lockChannel = supabase.channel(`lock-${sessionId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'live_sessions', filter: `id=eq.${sessionId}` },
        (payload) => { if (payload.new) setIsLocked(payload.new.whiteboard_locked); })
      .subscribe();

    const postitChannel = supabase.channel(`postits-live-${studentId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'postits', filter: `student_id=eq.${studentId}` },
        () => fetchPostits())
      .subscribe();

    const materialsChannel = supabase.channel(`materials-live-${studentId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'materials', filter: `student_id=eq.${studentId}` },
        () => fetchMaterials())
      .subscribe();

    const mistakesChannel = supabase.channel(`mistakes-live-${studentId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'mistakes', filter: `student_id=eq.${studentId}` },
        () => fetchMistakes())
      .subscribe();

    return () => {
      supabase.removeChannel(lockChannel);
      supabase.removeChannel(postitChannel);
      supabase.removeChannel(materialsChannel);
      supabase.removeChannel(mistakesChannel);
    };
  }, [sessionId, studentId]);

  const fetchPostits = async () => {
    const { data } = await supabase.from('postits').select('*').eq('student_id', studentId).order('created_at', { ascending: false });
    setPostits(data || []);
  };

  const fetchMaterials = async () => {
    const { data } = await supabase.from('materials').select('*').eq('student_id', studentId).order('created_at', { ascending: false });
    setMaterials(data || []);
  };

  const fetchMistakes = async () => {
    const { data } = await supabase.from('mistakes').select('*').eq('student_id', studentId).order('created_at', { ascending: false });
    setMistakes(data || []);
  };

  const toggleLock = async () => {
    const newLocked = !isLocked;
    setIsLocked(newLocked);
    await supabase.from('live_sessions').update({ whiteboard_locked: newLocked }).eq('id', sessionId);
  };

  const addPostit = async () => {
    if (!newNote.trim()) return;
    await supabase.from('postits').insert([{ student_id: studentId, content: newNote.trim(), author: role }]);
    setNewNote('');
  };

  const deletePostit = async (id) => {
    await supabase.from('postits').delete().eq('id', id);
  };

  const addMistake = async () => {
    if (!newMistake.trim()) return;
    await supabase.from('mistakes').insert([{ student_id: studentId, content: newMistake.trim(), author: role, session_id: sessionId }]);
    setNewMistake('');
  };

  const deleteMistake = async (id) => {
    await supabase.from('mistakes').delete().eq('id', id);
  };

  const uploadMaterial = async () => {
    if (!uploadFile) return;
    setUploading(true);
    const path = `${studentId}/${Date.now()}_${uploadFile.name}`;
    const { error: upErr } = await supabase.storage.from('materials').upload(path, uploadFile);
    if (upErr) { setUploadMsg('Upload failed: ' + upErr.message); setUploading(false); return; }
    const { data: urlData } = supabase.storage.from('materials').getPublicUrl(path);
    await supabase.from('materials').insert([{ student_id: studentId, file_name: uploadFile.name, file_url: urlData.publicUrl, file_path: path }]);
    setUploadFile(null);
    setUploading(false);
    setUploadMsg('Uploaded!');
    setTimeout(() => setUploadMsg(''), 2000);
  };

  const endSession = async () => {
    await supabase.from('live_sessions').update({ active: false }).eq('id', sessionId);
    onEnd();
  };

  const postitColours = [colours.yellow, colours.pink, colours.blue, colours.green, colours.purple];

  const s = {
    page: { height: '100vh', display: 'flex', flexDirection: 'column', background: '#f5f0ff' },
    header: { background: '#fff', borderBottom: '2px solid #f0e8ff', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
    tabs: { display: 'flex', background: '#fff', borderBottom: '2px solid #f0e8ff', padding: '0 20px', flexShrink: 0, overflowX: 'auto' },
    tab: (active) => ({ padding: '10px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: active ? 'bold' : 'normal', color: active ? colours.purpleMid : '#999', borderBottom: active ? `3px solid ${colours.purpleMid}` : '3px solid transparent', marginBottom: -2, whiteSpace: 'nowrap', flexShrink: 0 }),
    body: { flex: 1, padding: '16px 20px', overflow: 'auto', display: 'flex', flexDirection: 'column' },
    card: (bg) => ({ background: bg || '#fff', borderRadius: 14, padding: '14px 16px', marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }),
    textarea: { width: '100%', padding: '10px 12px', borderRadius: 10, border: '2px solid #e8e0f5', fontSize: 14, outline: 'none', minHeight: 64, resize: 'none', fontFamily: 'inherit' },
    addBtn: (bg, color) => ({ background: bg, color, border: 'none', borderRadius: 10, padding: '8px 18px', fontSize: 13, cursor: 'pointer', fontWeight: 'bold', marginTop: 8 }),
    postitGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))', gap: 12, marginTop: 10 },
    postitCard: (bg) => ({ background: bg, borderRadius: 14, padding: '12px', minHeight: 85, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', position: 'relative', fontSize: 13 }),
    deleteBtn: { position: 'absolute', top: 5, right: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#bbb' },
    matItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f0e8ff', cursor: 'pointer' },
    uploadRow: { display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' },
    mistakeItem: { display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 0', borderBottom: '1px solid #f0e8ff' },
  };

  const tabs = [
    { key: 'materials', label: '📄 Materials' },
    { key: 'whiteboard', label: '🖊️ Whiteboard' },
    { key: 'spelling', label: '🔤 Spelling' },
    { key: 'mistakes', label: '❌ Mistakes' },
    { key: 'postits', label: '📌 Notes' },
  ];

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 'bold', color: colours.purpleMid }}>
            🌟 Live Lesson {role === 'tutor' ? `— ${student}` : ''}
          </div>
          <div style={{ fontSize: 12, color: '#aaa' }}>{role === 'tutor' ? 'Tutor view' : 'Student view'}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {role === 'tutor' && (
            <button onClick={toggleLock} style={{
              background: isLocked ? colours.pink : colours.green,
              border: `2px solid ${isLocked ? colours.pinkMid : colours.greenMid}`,
              borderRadius: 10, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 'bold',
              color: isLocked ? '#c04060' : '#2e7d52',
            }}>
              {isLocked ? '🔒 Unlock drawing' : '🔓 Lock drawing'}
            </button>
          )}
          <button onClick={endSession} style={{ background: colours.pink, border: `2px solid ${colours.pinkMid}`, borderRadius: 10, padding: '6px 14px', cursor: 'pointer', fontSize: 12, color: '#c04060', fontWeight: 'bold' }}>
            {role === 'tutor' ? 'End session' : 'Leave lesson'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        {tabs.map(t => (
          <button key={t.key} style={s.tab(tab === t.key)} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {/* Body */}
      <div style={s.body}>

        {/* MATERIALS */}
        {tab === 'materials' && (
          <>
            {role === 'tutor' && (
              <div style={s.card(colours.purple)}>
                <div style={{ fontWeight: 'bold', marginBottom: 8, fontSize: 14 }}>Upload a file</div>
                <div style={s.uploadRow}>
                  <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                    onChange={e => setUploadFile(e.target.files[0])}
                    style={{ fontSize: 13 }} />
                  <button onClick={uploadMaterial} disabled={uploading || !uploadFile}
                    style={{ background: colours.purpleMid, color: '#fff', border: 'none', borderRadius: 10, padding: '8px 16px', cursor: 'pointer', fontWeight: 'bold', fontSize: 13 }}>
                    {uploading ? 'Uploading...' : 'Upload'}
                  </button>
                  {uploadMsg && <span style={{ color: colours.greenMid, fontSize: 13 }}>✅ {uploadMsg}</span>}
                </div>
              </div>
            )}
            {activePdf ? (
              <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 10 }}>
                <button onClick={() => setActivePdf(null)} style={{ ...s.addBtn(colours.blue, '#2266aa'), width: 'fit-content' }}>← Back to list</button>
                <iframe src={activePdf} style={{ flex: 1, borderRadius: 12, border: '2px solid #e8e0f5', minHeight: '60vh' }} title="material" />
              </div>
            ) : (
              <>
                <div style={{ fontWeight: 'bold', marginBottom: 10, color: '#555' }}>Files for this student</div>
                {materials.length === 0 && <div style={{ color: '#aaa', fontSize: 13 }}>No materials yet.{role === 'tutor' ? ' Upload one above.' : ' Your tutor will upload files here.'}</div>}
                {materials.map(mat => (
                  <div key={mat.id} style={s.matItem} onClick={() => setActivePdf(mat.file_url)}>
                    <span style={{ fontSize: 20 }}>📄</span>
                    <span style={{ color: colours.purpleMid, fontWeight: 'bold', fontSize: 14 }}>{mat.file_name}</span>
                  </div>
                ))}
              </>
            )}
          </>
        )}

        {/* WHITEBOARD */}
        {tab === 'whiteboard' && (
          <Whiteboard sessionId={sessionId} role={role} isLocked={isLocked} />
        )}

        {/* SPELLING */}
        {tab === 'spelling' && (
          <SpellingTest sessionId={sessionId} role={role} studentName={student} />
        )}

        {/* MISTAKES */}
        {tab === 'mistakes' && (
          <>
            <div style={s.card(colours.pink)}>
              <div style={{ fontWeight: 'bold', marginBottom: 6, fontSize: 14 }}>
                {role === 'tutor' ? `Record a mistake for ${student}` : 'Mistakes to work on'}
              </div>
              {role === 'tutor' && (
                <>
                  <textarea style={s.textarea} value={newMistake} onChange={e => setNewMistake(e.target.value)}
                    placeholder="e.g. Confused 'affect' and 'effect' — needs more practice" spellCheck={false} />
                  <br />
                  <button style={s.addBtn('#e05c7a', '#fff')} onClick={addMistake}>Add mistake</button>
                </>
              )}
            </div>
            {mistakes.length === 0 && <div style={{ color: '#aaa', fontSize: 13 }}>No mistakes recorded yet.</div>}
            {mistakes.map((m, i) => (
              <div key={m.id} style={{ ...s.card('#fff'), position: 'relative', borderLeft: '4px solid #f48fb1' }}>
                <div style={{ fontSize: 11, color: '#aaa', marginBottom: 4 }}>
                  {m.author === 'tutor' ? '👩‍🏫 Tutor' : '👧 Student'} · {new Date(m.created_at).toLocaleDateString('en-GB')}
                </div>
                <div style={{ fontSize: 14 }}>{m.content}</div>
                {role === 'tutor' && (
                  <button onClick={() => deleteMistake(m.id)} style={{ position: 'absolute', top: 10, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#ddd', fontSize: 18 }}>×</button>
                )}
              </div>
            ))}
          </>
        )}

        {/* NOTES / POSTITS */}
        {tab === 'postits' && (
          <>
            <div style={s.card(colours.yellow)}>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>
                {role === 'tutor' ? 'Add a note for your student 📌' : 'Write something to remember 📌'}
              </div>
              <textarea style={s.textarea} value={newNote} onChange={e => setNewNote(e.target.value)}
                placeholder={role === 'tutor' ? 'e.g. Great progress today!' : 'e.g. Revise question tags before next lesson'}
                spellCheck={false} autoCorrect="off" />
              <br />
              <button style={s.addBtn('#ffe082', '#5a4000')} onClick={addPostit}>Stick it! 📌</button>
            </div>
            <div style={{ fontSize: 12, color: '#bbb', marginBottom: 6 }}>Both you and your {role === 'tutor' ? 'student' : 'tutor'} can see these notes.</div>
            <div style={s.postitGrid}>
              {postits.map((p, i) => (
                <div key={p.id} style={s.postitCard(postitColours[i % postitColours.length])}>
                  <button style={s.deleteBtn} onClick={() => deletePostit(p.id)}>×</button>
                  <div style={{ fontSize: 11, color: '#999', marginBottom: 4 }}>{p.author === 'tutor' ? '👩‍🏫 Tutor' : '👧 Student'}</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{p.content}</div>
                </div>
              ))}
              {postits.length === 0 && <div style={{ color: '#aaa', fontSize: 13 }}>No notes yet.</div>}
            </div>
          </>
        )}

      </div>
    </div>
  );
}
